import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import CmsPublicApiClient, {
  CmsApiError,
} from "../../../services/api/cmsPublicApiClient";
import { mapNoticiaToListItemVM } from "../../../services/mappers/cmsPublicMappers";
import {
  NoticiaCategoryVM,
  NoticiaListItemVM,
} from "../../../types/viewModels/cmsPublic";

const service = new CmsPublicApiClient();
const PAGE_SIZE = 9;

const parsePage = (value: string | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

export function useNews() {
  const { t } = useTranslation("news");
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get("page"));
  const category = searchParams.get("categoria") || "";
  const query = searchParams.get("q") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noticias, setNoticias] = useState<NoticiaListItemVM[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  // El contrato no expone un endpoint de categorías, así que se derivan de las
  // noticias vistas y se acumulan para que el select no se vacíe al filtrar.
  const [knownCategories, setKnownCategories] = useState<NoticiaCategoryVM[]>(
    []
  );

  const buildErrorMessage = useCallback(
    (requestError: unknown) => {
      if (requestError instanceof CmsApiError) {
        if (requestError.status === 404) {
          return t("errors.notFound");
        }

        if (requestError.status === 400 || requestError.status === 422) {
          return t("errors.invalidQuery");
        }

        return t("errors.generic");
      }

      return t("errors.unexpected");
    },
    [t]
  );

  const fetchNoticias = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await service.getNoticias({
        page,
        pageSize: PAGE_SIZE,
        // Los params vacíos se omiten para no disparar INVALID_QUERY.
        ...(category ? { categoria: category } : {}),
        ...(query ? { q: query } : {}),
      });

      const mapped = response.data.map(mapNoticiaToListItemVM);
      setNoticias(mapped);
      setTotalPages(Math.max(response.meta.pagination.totalPages, 1));
      setKnownCategories((previous) => {
        const merged = new Map(
          previous.map((item) => [item.slug, item] as const)
        );
        mapped.forEach((noticia) =>
          noticia.categories.forEach((item) => merged.set(item.slug, item))
        );

        return Array.from(merged.values()).sort((a, b) =>
          (a.nombre || a.slug).localeCompare(b.nombre || b.slug)
        );
      });
    } catch (requestError) {
      setNoticias([]);
      setTotalPages(1);
      setError(buildErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [buildErrorMessage, category, page, query]);

  useEffect(() => {
    fetchNoticias();
  }, [fetchNoticias]);

  const updateParams = useCallback(
    (changes: Record<string, string>, resetPage: boolean) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(changes).forEach(([key, value]) => {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });

      if (resetPage) {
        next.delete("page");
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onPageChange = useCallback(
    (nextPage: number) =>
      updateParams({ page: nextPage > 1 ? String(nextPage) : "" }, false),
    [updateParams]
  );

  const onQueryChange = useCallback(
    (value: string) => updateParams({ q: value.trim() }, true),
    [updateParams]
  );

  const onCategoryChange = useCallback(
    (value: string) => updateParams({ categoria: value }, true),
    [updateParams]
  );

  const onClear = useCallback(
    () => updateParams({ q: "", categoria: "" }, true),
    [updateParams]
  );

  const hasFilters = useMemo(
    () => Boolean(query || category),
    [query, category]
  );

  return {
    loading,
    error,
    noticias,
    page,
    totalPages,
    query,
    category,
    categories: knownCategories,
    hasFilters,
    onPageChange,
    onQueryChange,
    onCategoryChange,
    onClear,
    retry: fetchNoticias,
  };
}

export default useNews;
